import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, BranchingKeyConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { GitBranch } from 'lucide-react-native';

interface BranchingKeyActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const BranchingKeyActivity: React.FC<BranchingKeyActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as BranchingKeyConfig;
    const specimens = useMemo(() => config.specimens || [], [config.specimens]);
    const nodes = useMemo(() => config.nodes || [], [config.nodes]);
    const leaves = useMemo(() => config.leaves || [], [config.leaves]);

    // Current specimen index being classified
    const [specimenIndex, setSpecimenIndex] = useState(0);
    // Current node ID in the decision tree
    const [currentNodeId, setCurrentNodeId] = useState<string>(() => {
        return nodes[0]?.id || '';
    });

    // History of classifications: specimenId -> leafId
    const [classifications, setClassifications] = useState<Record<string, string>>({});
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        config.prompt || 'Use the branching questions to classify each specimen.'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    const currentSpecimen = specimens[specimenIndex] || null;
    const currentNode = nodes.find((n) => n.id === currentNodeId) || null;

    // Check if reached a leaf
    const reachedLeaf = useMemo(() => {
        return leaves.find((l) => l.id === currentNodeId) || null;
    }, [leaves, currentNodeId]);

    const handleChoice = (isYes: boolean) => {
        if (!currentNode || hasCompleted) return;
        const nextId = isYes ? currentNode.yesNextId : currentNode.noNextId;
        setCurrentNodeId(nextId);
        setTrafficStatus('idle');
    };

    const handleConfirmLeaf = () => {
        if (!reachedLeaf || !currentSpecimen || hasCompleted) return;

        const isCorrect = currentSpecimen.targetLeafId === reachedLeaf.id;
        if (isCorrect) {
            const updated = { ...classifications, [currentSpecimen.id]: reachedLeaf.id };
            setClassifications(updated);

            if (specimenIndex + 1 < specimens.length) {
                setSpecimenIndex((prev) => prev + 1);
                setCurrentNodeId(nodes[0]?.id || '');
                setStatusMessage(`Correct! "${currentSpecimen.name}" is classified as a ${reachedLeaf.name}. Next specimen!`);
            } else {
                // All specimens classified
                setTrafficStatus('correct');
                setStatusMessage(`Brilliant Science! All ${specimens.length} specimens classified correctly!`);
                setHasCompleted(true);

                useStrokeStore.getState().addCustomAnswer({
                    activityId: activity.id,
                    value: updated,
                    mechanic: 'branching_key',
                    isCorrect: true,
                    score: 100,
                });

                setTimeout(() => {
                    onComplete();
                }, 1200);
            }
        } else {
            setTrafficStatus('incorrect');
            setStatusMessage(`Not quite right. "${currentSpecimen.name}" does not belong to ${reachedLeaf.name}. Let's trace the key again.`);
            setCurrentNodeId(nodes[0]?.id || '');
        }
    };

    return (
        <StageCard testID="branching-key-card">
            <View style={styles.container}>
                {/* Header */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <GitBranch size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>Science Branching Key</Text>
                        </View>
                        <Text style={styles.promptText}>{config.prompt || 'Classify the living specimens.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {currentNode ? (
                            <AudioPromptButton
                                promptText={currentNode.question}
                                activityId={activity.id}
                                testID="branching-key-audio-button"
                            />
                        ) : null}
                        <View style={styles.counterPill}>
                            <Text style={styles.counterText}>
                                {Object.keys(classifications).length} / {specimens.length} Classified
                            </Text>
                        </View>
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Main Workspace: Active Specimen Card */}
                {currentSpecimen && (
                    <View style={styles.specimenCard}>
                        <Text style={styles.specimenIcon}>{currentSpecimen.icon}</Text>
                        <View style={styles.specimenInfo}>
                            <Text style={styles.specimenLabel}>CURRENT SPECIMEN:</Text>
                            <Text style={styles.specimenName}>{currentSpecimen.name}</Text>
                        </View>
                    </View>
                )}

                {/* Decision Node or Final Leaf Confirmation */}
                {reachedLeaf ? (
                    <View style={styles.leafCard}>
                        <Text style={styles.leafIcon}>{reachedLeaf.icon}</Text>
                        <Text style={styles.leafQuestion}>Classification Reached:</Text>
                        <Text style={styles.leafName}>{reachedLeaf.name}</Text>
                        <JuicyButton
                            label="CONFIRM CLASSIFICATION"
                            variant="primary"
                            size="medium"
                            onPress={handleConfirmLeaf}
                            disabled={hasCompleted}
                            testID="confirm-leaf-button"
                        />
                    </View>
                ) : currentNode ? (
                    <View style={styles.questionCard}>
                        <Text style={styles.questionText}>{currentNode.question}</Text>
                        <View style={styles.choiceButtonsRow}>
                            <TouchableOpacity
                                style={[styles.choiceBtn, styles.choiceBtnYes]}
                                onPress={() => handleChoice(true)}
                                disabled={hasCompleted}
                                accessibilityLabel="Answer Yes"
                                testID="choice-yes-button"
                            >
                                <Text style={styles.choiceBtnEmoji}>👍</Text>
                                <Text style={[styles.choiceBtnText, styles.choiceBtnTextYes]}>YES</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.choiceBtn, styles.choiceBtnNo]}
                                onPress={() => handleChoice(false)}
                                disabled={hasCompleted}
                                accessibilityLabel="Answer No"
                                testID="choice-no-button"
                            >
                                <Text style={styles.choiceBtnEmoji}>👎</Text>
                                <Text style={[styles.choiceBtnText, styles.choiceBtnTextNo]}>NO</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                ) : null}

                {/* Status Bar */}
                <View style={styles.statusBar}>
                    <Text style={styles.statusText}>{statusMessage}</Text>
                </View>
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    counterPill: {
        backgroundColor: '#1E293B',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    counterText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    specimenCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 12,
    },
    specimenIcon: {
        fontSize: 36,
    },
    specimenInfo: {
        flex: 1,
    },
    specimenLabel: {
        fontSize: 11,
        fontWeight: '800',
        color: '#64748B',
    },
    specimenName: {
        fontSize: 18,
        fontWeight: '900',
        color: '#0F172A',
    },
    questionCard: {
        backgroundColor: '#EFF6FF',
        padding: 18,
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#BFDBFE',
        alignItems: 'center',
        gap: 16,
    },
    questionText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1E3A8A',
        textAlign: 'center',
        lineHeight: 24,
    },
    choiceButtonsRow: {
        flexDirection: 'row',
        gap: 20,
    },
    choiceBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 14,
        minWidth: 120,
        justifyContent: 'center',
        borderWidth: 2,
    },
    choiceBtnYes: {
        backgroundColor: '#DCFCE7',
        borderColor: '#86EFAC',
    },
    choiceBtnNo: {
        backgroundColor: '#FEE2E2',
        borderColor: '#FECDD3',
    },
    choiceBtnEmoji: {
        fontSize: 20,
    },
    choiceBtnText: {
        fontSize: 16,
        fontWeight: '900',
    },
    choiceBtnTextYes: {
        color: '#15803D',
    },
    choiceBtnTextNo: {
        color: '#B91C1C',
    },
    leafCard: {
        backgroundColor: '#F0FDF4',
        padding: 20,
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#86EFAC',
        alignItems: 'center',
        gap: 10,
    },
    leafIcon: {
        fontSize: 40,
    },
    leafQuestion: {
        fontSize: 13,
        fontWeight: '700',
        color: '#166534',
    },
    leafName: {
        fontSize: 22,
        fontWeight: '900',
        color: '#14532D',
        marginBottom: 8,
    },
    statusBar: {
        backgroundColor: '#F1F5F9',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
});
